import CrossItem from '@/components/CrossItem';
import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { useGetRelatedEntityData } from '@/hooks/related-entity-data.hooks';
import { TGroupSchema } from '@/schemas/group/group.schema';
import { getGroupById } from '@/api/group/group';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import Loader from '@/components/Loading/Loader';
import { getTranslatedItem } from '@/utils/language.utils';

export const VehiculeCheckTypeTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  const { entities: groups, relatedEntitiesLoading } =
    useGetRelatedEntityData<TGroupSchema>({
      data,
      relatedEntityName: 'groupId',
      getEntityById: getGroupById,
    });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={6}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          onDeactivate={handleDeactivate}
          onDownload={() => {}}
          onCreate={() => navigate('/vehicule-check-type/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        const translatedVehiculeCheckType = getTranslatedItem(
          entry?.translatedVehiculeCheckTypes
        );

        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            <TableCell
              tdClassName="sticky-select-column"
              content={
                <Checkbox
                  checked={Boolean(
                    selectedTableData?.find((item) => item?.id === entry?.id)
                  )}
                  onChange={() => onSelectedItem(entry?.id)}
                />
              }
            />
            <TableCell
              content={translatedVehiculeCheckType?.name || entry?.name}
            />
            <TableCell
              content={
                groups[entry?.groupId] && (
                  <div className="flex gap-2">
                    <div className="w-6 h-6 min-w-6 flex items-center justify-center relative text-sm text-brand ring-1 ring-brand-clarity bg-brand-light rounded-full">
                      {groups[entry?.groupId]?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="self-center">
                      {groups[entry?.groupId]?.name}
                    </span>
                  </div>
                )
              }
            />
            <TableCell content={<CrossItem isSuccess={entry?.active} />} />
            <TableCell
              content={
                <div>
                  {entry?.personTypes?.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {entry?.personTypes?.map((type) => (
                        <div
                          key={type}
                          className="flex gap-2 text-slate-500 text-2sm font-semibold uppercase"
                        >
                          <i
                            className={twMerge(
                              'h-0 ki-outline text-[15px] text-black',
                              type === ActionUserType.PROFESSIONAL
                                ? 'ki-office-bag'
                                : 'ki-user'
                            )}
                          />
                          {type === ActionUserType.PROFESSIONAL
                            ? t('common:professional')
                            : t('common:individual')}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>--</>
                  )}
                </div>
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={`/vehicule-check-type/edit/${entry?.id}`}
                  />
                  <SubMenuItem
                    text={t('common:deactivate')}
                    disabled={!entry?.active || pendingDeactivate}
                    leftIconClassName="ki-trash !text-[19px]"
                    textClassName="line-clamp-1"
                    onClick={() => onDeactivate([entry])}
                  />
                </TableMenu>
              }
            />
          </tr>
        );
      })}
      {relatedEntitiesLoading && <Loader />}
    </>
  );
};
